import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { Collapse, Divider, IconButton, InputBase, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import { theme } from 'theme';

import { DialogTitleWithClose } from 'ui';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { portfolioUploadSchema } from 'shared/validations';
import { downloadService } from 'services';
import { generateKey, putNotifyError } from 'shared/functions';
import { uploadRequest } from 'services/esg/requestService';
import { get } from 'lodash';
import { useNotifications } from 'shared/hooks';

const useStyles = makeStyles({
  downloadTemplateContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  downloadButtonContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  input: {
    display: 'none',
  },
  uploadContainer: {
    padding: '2px 4px',
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
  },
  fileUploadText: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  summaryContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textCenter: {
    textAlign: 'center',
  },
  checkIcon: {
    fontSize: 80,
  },
  score: {
    fontSize: 40,
    fontWeight: 'bold',
  },
  selectButton: {
    width: 150,
    height: 100,
    textTransform: 'inherit',
    border: 'none',
    color: theme.palette.text.primary,
    boxShadow: '0 4px 4px 2px rgba(155, 155, 155, 0.3)',
    padding: '0 35px',
  },
  selectTitle: {
    fontSize: 15,
  },
});

export const steps = {
  UPLOAD: 0,
  SUMMARY: 1,
};

const RequestEsgScoreModal = ({ open, onClose, onRequested, selectIds }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);
  const { notifyError } = useNotifications();
  const [fileUploadInputRef, setFileUploadInputRef] = useState(null);
  const [data, setData] = useState(null);
  const [step, setStep] = useState(steps.UPLOAD);
  const [showErrors, setShowErrors] = useState(false);

  const sampleTemplateUrl = '/esg/api/generate-financial-template/';

  const closeModal = () => {
    setStep(steps.UPLOAD);
    onClose();
  };

  const downloadErrorHandler = () => {
    putNotifyError(t('Failed to download sample portfolio template'));
  };

  const fileUploadInputRefChanged = element => setFileUploadInputRef(element);

  const handleDownloadTemplateClick = () => {
    setIsPending(true);
    downloadService({
      path: sampleTemplateUrl,
      name: 'esg-financial-data-template.xlsx',
      options: { body: JSON.stringify(selectIds), method: 'POST' },
      onError: downloadErrorHandler,
    });
    setIsPending(false);
  };

  const handleSubmit = async payload => {
    setIsPending(true);
    try {
      const uploadData = await uploadRequest(payload && payload.fileUploadInput);
      setData({ ...uploadData, fileName: payload.fileUploadInput.name });
    } catch (e) {
      setError(e.message);
      setData({ errors: e.responseData.errors || [] });
    }
    setStep(steps.SUMMARY);
    setIsPending(false);
    if (onRequested) {
      onRequested();
    }
  };

  const updateUploadBoxData = (setFieldValueFunc, file, text) => {
    if (!file && fileUploadInputRef) {
      fileUploadInputRef.value = null;
    }
    setFieldValueFunc('fileUploadInput', file);
    setFieldValueFunc('fileUploadText', text);
  };

  const toggleErrorsList = () => {
    setShowErrors(!showErrors);
  };

  useEffect(() => {
    if (error) {
      notifyError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const requestFormInitialValues = {
    description: '',
    origin: window.location.href,
    fileUploadText: '',
    fileUploadInput: null,
  };

  const requestForm = (
    <Formik
      key="esg-score-request"
      initialValues={requestFormInitialValues}
      onSubmit={payload => handleSubmit(payload)}
      validationSchema={portfolioUploadSchema(['csv', 'xls', 'xlsx'], 'File upload is required')}
    >
      {({ values, errors, touched, setFieldValue }) => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>
                  {t(
                    `The upload of your data will allow you to calculate the ESG impact score of this particular entity`,
                  )}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography>
                  {t(
                    `To prepare the upload, please create an upload file similar to the template provided below 
                    and upload it to ScopeOne following the portfolio wizard below.`,
                  )}
                </Typography>
              </Box>
              <Divider />
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography>{`${t('File format')}: .csv, .xls, .xlsx`}</Typography>
                  <Typography>{`${t('Max size')}: 100 ${t('ENTITIES').toLowerCase()}`}</Typography>
                  <Typography>{`${t(
                    'Column heading',
                  )}: esgID, company_name, company_type, financial_year, 
                    data_type, currency, value, codes, value_type
`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={handleDownloadTemplateClick}
                    variant="outlined"
                    disabled={isPending}
                  >
                    {t('DOWNLOAD_TEMPLATE')}
                  </Button>
                </Box>
              </div>
              <Divider />
              <ErrorMessage name="fileUploadInput">
                {message => (
                  <Box mt={2}>
                    <Typography color="error">{message}</Typography>
                  </Box>
                )}
              </ErrorMessage>
              <Paper className={classes.uploadContainer}>
                <input
                  className={classes.input}
                  id="file-upload"
                  name="file-upload"
                  ref={fileUploadInputRefChanged}
                  type="file"
                  onChange={event => {
                    updateUploadBoxData(
                      setFieldValue,
                      event.currentTarget.files[0],
                      get(event.currentTarget.files, '[0].name', ''),
                    );
                  }}
                />
                <Field name="fileUploadText">
                  {({ field }) => (
                    <InputBase
                      {...field}
                      className={classes.fileUploadText}
                      placeholder={t('UPLOAD.NO_FILE_SELECTED')}
                      error={errors.fileUploadInput && touched.fileUploadText}
                      readOnly
                    />
                  )}
                </Field>
                <IconButton
                  type="submit"
                  disabled={!fileUploadInputRef || !fileUploadInputRef.value}
                  onClick={() => {
                    updateUploadBoxData(setFieldValue, null, '');
                  }}
                  size="large">
                  <DeleteIcon />
                </IconButton>
                <label htmlFor="file-upload">
                  <Button variant="contained" color="primary" component="span">
                    {t('UPLOAD.BROWSE')}
                  </Button>
                </label>
              </Paper>
            </DialogContent>
            <DialogActions>
              <div style={{ flex: '1 0 0' }} />
              <Button variant="outlined" onClick={closeModal}>
                {t('CANCEL')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || !get(values, 'fileUploadInput')}
              >
                {t('NEXT')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const summaryStep = (
    <>
      <DialogContent>
        <Box mb={2}>
          {get(data, 'success', false) ? (
            <Typography variant="h6" className={classes.textCenter}>
              <Box
                width={140}
                height={140}
                borderRadius="50%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginY={2}
                marginX="auto"
                boxShadow={theme.customShadows[0]}
                bgcolor={theme.palette.custom.verySoftCyan}
              >
                <CheckIcon className={classes.checkIcon} />
              </Box>
              Upload Successfull
            </Typography>
          ) : (
            <Typography variant="h6" className={classes.textCenter}>
              <Collapse in={!showErrors} timeout="auto" unmountOnExit>
                <Box
                  width={140}
                  height={140}
                  borderRadius="50%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  marginY={2}
                  marginX="auto"
                  boxShadow={theme.customShadows[0]}
                  bgcolor={theme.palette.custom.verySoftPink}
                >
                  <Close className={classes.checkIcon} />
                </Box>
              </Collapse>
              {t('ESG.SCORE_REQUEST.REQUEST_FAILED')}
            </Typography>
          )}

          {data?.errors?.length ? (
            <>
              <Typography className={classes.textCenter}>
                <Button color="primary" onClick={toggleErrorsList}>
                  {t('ERRORS')} ({data?.errors?.length}){' '}
                  {showErrors ? <ArrowDropUp /> : <ArrowDropDown />}
                </Button>
              </Typography>
              <Collapse in={showErrors} timeout="auto" unmountOnExit>
                <Paper>
                  <ul>
                    {data?.errors?.map((item, i) => {
                      return (
                        <li key={generateKey(i, 'error-message')}>
                          {item.line ? `line ${item.line}: ` : ''} {item.message}
                        </li>
                      );
                    })}
                  </ul>
                </Paper>
              </Collapse>
            </>
          ) : (
            undefined
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <div style={{ flex: '1 0 0' }} />
        <Button variant="contained" onClick={closeModal}>
          {t('DONE')}
        </Button>
      </DialogActions>
    </>
  );

  const title = t('ESG.SCORE_REQUEST.TITLE');

  return (
    <>
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitleWithClose onClose={closeModal}>{title}</DialogTitleWithClose>
        {step === steps.UPLOAD ? requestForm : ''}
        {step === steps.SUMMARY ? summaryStep : ''}
      </Dialog>
    </>
  );
};

RequestEsgScoreModal.defaultProps = {
  open: false,
  onClose: () => null,
  onRequested: () => null,
};

RequestEsgScoreModal.propTypes = {
  selectIds: PropTypes.array.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onRequested: PropTypes.func,
};

export default RequestEsgScoreModal;
